import {
    ClipboardEvent,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    ArrowDown,
    CircleDashed,
    Plus,
    Sparkles,
    Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

import { useReportUnsavedChanges } from '@/hooks/useUnsavedChangesGuard';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';

import { Cell } from './CellInput';
import { EditorFooter, loadErrorMessage } from './EditorFooter';
import { LoadError } from './LoadError';
import { PanelSkeleton } from './LoadingSkeletons';
import { LinkifiedText } from './LinkifiedText';
import {
    LINE_ITEM_MODEL_CLASS,
    useGtRows,
    useGtSuggestedRows,
    useRowSchema,
    useSaveGtRows,
} from './api';
import {
    ColumnDef,
    LINE_ITEM_DEFAULT_COLUMN_KEYS,
    NON_COLUMN_KEYS,
    blankPayloadFor,
    seedCustomDefaults,
    deriveColumns,
    isPlainRenderableValue,
    toColumnDef,
    visibleColumns,
} from './columns';
import {
    customValueError,
    emptyRequiredLabels,
    isBlankValue,
    nullBlankCustomValues,
} from './customFields';
import {
    cellValueToRaw,
    coerceCell,
    parseClipboardTable,
} from './gridClipboard';
import { Hint } from './Hint';
import { GtRow, GtRowPayload } from './types';
import {
    answerSetVersion,
    GridValidation,
    useGridEditor,
} from './useGridEditor';
import { useGridRovingFocus } from './useGridRovingFocus';

export interface DraftRow {
    key: string;
    /** Raw snake_case payload bag — the source of truth for this row. */
    payload: GtRowPayload;
}

/** Sticky-left classes: opaque background so scrolled cells don't show through. */
const ROW_NUMBER_CELL_CLASS = 'sticky left-0 w-16 min-w-16 bg-background px-1';
const FROZEN_COLUMN_CLASS = 'sticky left-16 bg-background border-r';

/** Whether a required cell is empty (shared by the cell error and {@link validateDrafts}); a filled number, boolean, date or choice counts as filled. */
function isBlankRequired(value: unknown, kind: ColumnDef['kind']): boolean {
    return isBlankValue(value, kind);
}

let nextKey = 0;
const makeKey = () => `row-${nextKey++}`;

/**
 * "Item No and Description are required." / "Item No is required." /
 * `null` when nothing is required — the help text under the grid header,
 * built from whichever columns the doc's schema marks required instead of a
 * hardcoded item_no/description pair.
 */
export function requiredColumnsSentence(labels: string[]): string | null {
    if (labels.length === 0) return null;
    if (labels.length === 1) return `${labels[0]} is required.`;
    const verb = 'are required.';
    if (labels.length === 2) return `${labels[0]} and ${labels[1]} ${verb}`;
    return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]} ${verb}`;
}

/**
 * Layer 5 editor: an editable grid of extracted rows for a doc, rendered off
 * the doc's resolved row-truth model (`useRowSchema`) rather than a hardcoded
 * `LineItem` shape — a status-report doc gets its own columns, a `LineItem`
 * doc (still the fallback for any doc type without a registered model) keeps
 * rendering exactly as before.
 *
 * Built on the shadcn `Table` primitive — `@tanstack/react-table` is not a
 * dependency. Rows are memoised; if a doc ever carries very large row counts
 * this plain (non-virtualised) table is the place to revisit (flagged, not
 * fixed — no virtualization dep is pulled in here).
 */
export function RowEditor({ docId }: { docId: number }) {
    // Keyed so drafts, a save conflict and the saved version reset when the route reuses this for another doc.
    return <RowEditorForDoc key={docId} docId={docId} />;
}

function RowEditorForDoc({ docId }: { docId: number }) {
    const {
        data: schema,
        isLoading: schemaLoading,
        isError: schemaIsError,
        isRefetching: schemaRefetching,
        refetch: refetchSchema,
    } = useRowSchema(docId);
    const modelClass = schema?.modelClass ?? null;

    // The doc's own columns, translated from the schema endpoint's raw
    // descriptors — `[]` while the schema hasn't resolved yet, which is fine:
    // every consumer below is gated by `isLoading`/`schemaIsError` before it
    // matters.
    const baseColumns = useMemo(
        () => (schema ? schema.columns.map(toColumnDef) : []),
        [schema],
    );
    // Built-in required columns keep their always-on error; custom columns are
    // checked separately and only report after a save attempt (see `saveAttempted`).
    const requiredColumns = useMemo(
        () =>
            (schema?.columns ?? [])
                .filter((c) => c.required && !c.custom)
                .map((c) => ({
                    key: c.key,
                    label: c.label,
                    kind: toColumnDef(c).kind,
                })),
        [schema],
    );
    const customColumns = useMemo(
        () => baseColumns.filter((c) => c.custom),
        [baseColumns],
    );
    const earlierKeys = useMemo(() => schema?.earlierKeys ?? [], [schema]);

    // A row saved under an older field-group version opens without errors; the
    // custom columns' errors appear once Save has been pressed.
    // Held as the doc it was pressed on, so opening another doc resets it.
    const [attemptedDocId, setAttemptedDocId] = useState<number | null>(null);
    const saveAttempted = attemptedDocId === docId;

    const {
        data: existing,
        isLoading: rowsLoading,
        isError: rowsError,
        isRefetching: rowsRefetching,
        refetch: refetchRows,
    } = useGtRows(docId, modelClass);
    // Without the saved rows the grid is empty, and saving it would delete them.
    const rowsLoadFailed = existing === undefined && rowsError;
    const save = useSaveGtRows(docId, modelClass);
    const suggested = useGtSuggestedRows(docId, modelClass);

    // Back to "opened, not yet saved" once a save lands.
    useEffect(() => {
        if (save.isSuccess) setAttemptedDocId(null);
    }, [save.isSuccess]);
    const isLoading = schemaLoading || rowsLoading;

    /** The blank row "Add row" appends — shared with paste, which appends the
     * same shape when a pasted block runs past the end of the current drafts.
     * Seeds every column the doc's model has (not just the required ones), so
     * a freshly added row already carries the right empty value per kind. */
    const makeBlankDraftRow = useCallback(
        (): DraftRow => ({
            key: makeKey(),
            payload: blankPayloadFor(baseColumns),
        }),
        [baseColumns],
    );

    const validate = useCallback(
        (rows: DraftRow[]) =>
            validateDrafts(rows, requiredColumns, customColumns, saveAttempted),
        [requiredColumns, customColumns, saveAttempted],
    );

    const {
        drafts,
        setDrafts,
        updateDraft,
        removeDraft,
        addDraft,
        errors,
        isDirty,
        clearCount,
        onSave,
        conflict,
        reloadSaved,
    } = useGridEditor<DraftRow, GtRowPayload[], GtRow[] | undefined>({
        savedCount: existing?.length ?? 0,
        savedVersion: existing && answerSetVersion(existing),
        reload: refetchRows,
        existing,
        seed: (rows) =>
            rows.map((r) => ({
                key: makeKey(),
                payload: seedCustomDefaults(r.payload, baseColumns),
            })),
        validate,
        save,
        successMessage: 'Rows saved',
        entityType: 'rows',
    });

    useReportUnsavedChanges(isDirty);

    const onSaveClick = useCallback(
        (options?: { confirmClear?: boolean }) => {
            if (!saveAttempted) {
                const { errors: all } = validateDrafts(
                    drafts,
                    requiredColumns,
                    customColumns,
                    true,
                );
                if (all.length > 0) {
                    setAttemptedDocId(docId);
                    toast.error(all[0]);
                    return;
                }
            }
            onSave(options);
        },
        [saveAttempted, docId, drafts, requiredColumns, customColumns, onSave],
    );

    // Whether truth has ever been saved for this doc — distinguishes the
    // cold-start "Prefill" action (nothing to lose) from "Reset to model
    // answer" (truth exists, so overwriting it is confirmed first). `existing`
    // is `[]` (not `undefined`) once the query resolves with no rows, so this
    // only flips true once real truth has actually been saved.
    const hasTruth = (existing?.length ?? 0) > 0;

    // Whether the last prefill attempt came back with no suggestions at all
    // (e.g. a GT-importer doc that never ran through extraction) — surfaced
    // as a subtle note, not an error, alongside the blank-row fallback.
    const [noExtractionAvailable, setNoExtractionAvailable] = useState(false);
    const [resetDialogOpen, setResetDialogOpen] = useState(false);

    // Pull the model's own extraction and seed the drafts with it. Used by
    // both the cold-start "Prefill" button (drafts are empty, so there's
    // nothing to lose) and the confirmed "Reset to model answer" action.
    // Falls back to a single blank row — the same shape "Add row" appends —
    // when the doc never produced an extraction, rather than leaving the
    // grid empty or surfacing an error for what isn't a failure.
    const applySuggestions = useCallback(async () => {
        if (modelClass !== null && modelClass !== LINE_ITEM_MODEL_CLASS) {
            // The `/rows/suggested` endpoint only ever returns `LineItem`-shaped
            // rows (see `useGtSuggestedRows`'s docstring), regardless of the
            // doc's actual resolved row model. Seeding a non-LineItem grid with
            // those fields would silently drop them on save (e.g.
            // `StatusReportRow`'s `extra="ignore"`), so this is blocked here as
            // defense in depth even though both callers also disable their
            // button for this case.
            toast.error('Prefill is not available for this document type yet.');
            return;
        }
        const result = await suggested.refetch();
        if (result.isError) {
            // A failed refetch (network error, 500, timeout) is NOT the same
            // as "no extraction found" — surface it and leave existing
            // drafts untouched, especially on the Reset path, whose dialog
            // promises nothing is lost until Save.
            toast.error('Failed to load the model extraction. Try again.');
            return;
        }
        const rows = result.data ?? [];
        if (rows.length === 0) {
            setNoExtractionAvailable(true);
            setDrafts([makeBlankDraftRow()]);
            return;
        }
        setNoExtractionAvailable(false);
        setDrafts(
            rows.map((r) => ({
                key: makeKey(),
                payload: seedCustomDefaults(r, baseColumns),
            })),
        );
    }, [suggested, setDrafts, makeBlankDraftRow, modelClass, baseColumns]);

    const onConfirmReset = useCallback(() => {
        setResetDialogOpen(false);
        void applySuggestions();
    }, [applySuggestions]);

    // `deriveColumns` needs to know WHICH extra (non-known) keys are present
    // across the drafts AND whether each key's first-seen value is renderable
    // as a plain text cell (see `isPlainRenderableValue`) — but memoising on
    // `drafts` itself recomputes (and produces a new `columns` array
    // reference) on every keystroke, since `drafts` changes identity on any
    // edit. That defeats the `React.memo` on `RowCells` below. Instead
    // memoise on a signature string of the extra-key SET plus each key's
    // renderability, which is stable across edits that don't add/remove a key
    // or flip a key between renderable and non-renderable.
    //
    // Renderability must be part of the signature, not just the key names:
    // `deriveColumns` walks rows in order and uses the FIRST-seen value per
    // key to decide whether that key becomes a column, so removing/reordering
    // rows can change which value is "first seen" for a shared extra key
    // without changing the set of key names — e.g. row A has a renderable
    // string for `foo`, row B has a non-renderable object for `foo`; removing
    // row A flips `foo`'s promoted-to-column outcome even though the key set
    // (`{foo}`) is unchanged. A name-only signature would miss that and leave
    // `columns` stale. JSON.stringify (not a comma-join) avoids signature
    // collisions from key names that themselves contain the delimiter.
    const knownKeys = useMemo(
        () => new Set(baseColumns.map((c) => c.key)),
        [baseColumns],
    );
    const extraKeysSignature = useMemo(() => {
        const firstSeenRenderable = new Map<string, boolean>();
        const earlierPresent = new Set<string>();
        for (const row of drafts) {
            for (const [key, value] of Object.entries(row.payload)) {
                if (knownKeys.has(key) || NON_COLUMN_KEYS.has(key)) continue;
                if (earlierKeys.includes(key)) {
                    earlierPresent.add(key);
                    continue;
                }
                if (firstSeenRenderable.has(key)) continue;
                firstSeenRenderable.set(key, isPlainRenderableValue(value));
            }
        }
        return JSON.stringify([
            [...firstSeenRenderable.entries()].sort(([a], [b]) =>
                a.localeCompare(b),
            ),
            [...earlierPresent].sort(),
        ]);
    }, [drafts, knownKeys, earlierKeys]);

    // The rendered grid = the doc's model columns (from `baseColumns`) + any
    // extra payload keys present in the data (so a field the schema doesn't
    // mention still shows up instead of being silently dropped). Unknown keys
    // are preserved on save regardless of whether they surface as a column
    // here. `drafts` is deliberately omitted from the deps below —
    // `extraKeysSignature` (derived from `drafts` above) is the real dependency,
    // and depending on `drafts` directly would recompute `columns` (and defeat
    // the `RowCells` memoisation) on every keystroke.

    const columns = useMemo(
        () => deriveColumns(drafts, baseColumns, earlierKeys),
        [extraKeysSignature, baseColumns, earlierKeys],
    );

    // Column-key → kind lookup for the ACTIVE grid (base + extra columns),
    // used by paste/fill-down to coerce a raw string the same way `CellInput`
    // would render it — built once per `columns` change rather than per cell.
    const columnKinds = useMemo(
        () => new Map(columns.map((c) => [c.key, c.kind] as const)),
        [columns],
    );

    // Custom booleans can be unset, so a blank paste or fill-down leaves them null rather than false.
    const nullableBoolKeys = useMemo(
        () =>
            new Set(
                columns
                    .filter((c) => c.custom && c.kind === 'bool')
                    .map((c) => c.key),
            ),
        [columns],
    );

    // LineItem docs open on a short default set; the rest sit behind a toggle.
    const [showAllColumns, setShowAllColumns] = useState(false);
    const visibleCols = useMemo(
        () =>
            visibleColumns(
                columns,
                modelClass === LINE_ITEM_MODEL_CLASS
                    ? LINE_ITEM_DEFAULT_COLUMN_KEYS
                    : null,
                showAllColumns,
            ),
        [columns, modelClass, showAllColumns],
    );
    const hiddenColumnCount = columns.length - visibleCols.length;
    const requiredKeys = useMemo(
        () => new Set(requiredColumns.map((c) => c.key)),
        [requiredColumns],
    );

    // Stable identities so the memoised `RowCells` only re-renders the row
    // whose payload actually changed (these are passed as props to every row).
    // `setCell` adapts the grid hook's `updateDraft` (a whole-row patch) into a
    // single-field payload edit; `updateDraft`/`removeDraft` are already stable.
    const setCell = useCallback(
        (rowKey: string, field: string, value: unknown) =>
            updateDraft(rowKey, (prev) => ({
                payload: { ...prev.payload, [field]: value },
            })),
        [updateDraft],
    );

    // Paste a rectangular block (Excel TSV) starting at the anchor cell,
    // mapping it rightward onto visible columns and downward onto rows —
    // extending the draft list with blank rows (same shape "Add row" appends)
    // when the block runs past the end. A single `setDrafts` batch (rather
    // than one `updateDraft` per cell) keeps a multi-row paste one state
    // update instead of N, and lets the row-extension logic see the rows it
    // just added within the same pass. Columns past the last visible one are
    // dropped rather than wrapped — pasting a wider block than the grid is
    // treated as "paste what fits", not an error.
    const onPasteBlock = useCallback(
        (rowKey: string, colKey: string, clipboardText: string) => {
            // An empty clipboard (e.g. nothing was ever copied) should be a
            // no-op, not blank out the anchor cell — `parseClipboardTable`
            // always returns at least one (possibly single-empty-string) row,
            // so this has to be checked before parsing, not after.
            if (clipboardText === '') return;
            const block = parseClipboardTable(clipboardText);
            const anchorColIndex = visibleCols.findIndex(
                (c) => c.key === colKey,
            );
            if (anchorColIndex === -1) return;

            setDrafts((current) => {
                const anchorIndex = current.findIndex((r) => r.key === rowKey);
                if (anchorIndex === -1) return current;

                const next = [...current];
                block.forEach((rowCells, i) => {
                    const targetIndex = anchorIndex + i;
                    while (targetIndex >= next.length) {
                        next.push(makeBlankDraftRow());
                    }
                    const patch: GtRowPayload = {};
                    rowCells.forEach((raw, j) => {
                        const targetColumn = visibleCols[anchorColIndex + j];
                        if (!targetColumn || targetColumn.readOnly) return;
                        patch[targetColumn.key] = coerceCell(
                            raw,
                            targetColumn.key,
                            columnKinds,
                            nullableBoolKeys,
                        );
                    });
                    next[targetIndex] = {
                        ...next[targetIndex],
                        payload: { ...next[targetIndex].payload, ...patch },
                    };
                });
                return next;
            });
        },
        [
            visibleCols,
            columnKinds,
            nullableBoolKeys,
            setDrafts,
            makeBlankDraftRow,
        ],
    );

    // Copy the anchor cell's current value down every row below it in the
    // same column. Round-trips the value through `cellValueToRaw` +
    // `coerceCell` (rather than copying the JS value by reference) so a
    // filled-down list/number/bool lands exactly like a typed or pasted one —
    // `coerceCell` is the single source of coercion truth for both paths.
    const onFillDown = useCallback(
        (rowKey: string, colKey: string, kind: ColumnDef['kind']) => {
            setDrafts((current) => {
                const anchorIndex = current.findIndex((r) => r.key === rowKey);
                if (anchorIndex === -1) return current;

                const raw = cellValueToRaw(
                    current[anchorIndex].payload[colKey],
                    kind,
                    nullableBoolKeys.has(colKey),
                );
                const coerced = coerceCell(
                    raw,
                    colKey,
                    columnKinds,
                    nullableBoolKeys,
                );
                return current.map((row, i) =>
                    i <= anchorIndex
                        ? row
                        : {
                              ...row,
                              payload: { ...row.payload, [colKey]: coerced },
                          },
                );
            });
        },
        [setDrafts, columnKinds, nullableBoolKeys],
    );

    // Keyboard navigation (roving tabindex, pure DOM manipulation — see
    // `useGridRovingFocus`'s docstring for why this causes zero re-renders).
    const onEnterAddRow = useCallback(
        () => addDraft(makeBlankDraftRow),
        [addDraft, makeBlankDraftRow],
    );
    const { gridRef, handleGridFocus, handleGridKeyDown } = useGridRovingFocus(
        drafts.length,
        visibleCols.length,
        onEnterAddRow,
    );

    const unusedRowCount = useMemo(
        () =>
            drafts.filter(
                (d) => emptyRequiredLabels(customColumns, d.payload).length > 0,
            ).length,
        [drafts, customColumns],
    );
    const requiredSentence = requiredColumnsSentence(
        requiredColumns.map((c) => c.label),
    );
    const groupDescription = schema?.fieldGroup?.description ?? '';

    return (
        <div className="flex flex-col h-full min-h-0" data-testid="RowEditor">
            <div className="px-4 pt-4 pb-3 border-b shrink-0">
                <h2 className="text-base font-semibold text-gray-900">
                    {schema?.title ?? 'Rows'}
                </h2>
                <p className="text-xs text-muted-foreground mt-1">
                    Cross-check each row against the file preview.
                    {requiredSentence ? ` ${requiredSentence}` : ''}
                </p>
                {groupDescription && (
                    <p
                        className="text-xs text-muted-foreground mt-1 whitespace-pre-line"
                        data-testid="RowEditor__groupDescription"
                    >
                        <LinkifiedText text={groupDescription} />
                    </p>
                )}
            </div>

            <div className="flex-1 overflow-auto p-4">
                {schemaIsError ? (
                    <LoadError
                        what="the row schema for this document"
                        onRetry={() => void refetchSchema()}
                        isRetrying={schemaRefetching}
                        data-testid="RowEditor__schemaError"
                    />
                ) : isLoading ? (
                    <PanelSkeleton rows={5} data-testid="RowEditor__loading" />
                ) : rowsLoadFailed ? (
                    <LoadError
                        what="the saved rows for this document"
                        onRetry={() => void refetchRows()}
                        isRetrying={rowsRefetching}
                        data-testid="RowEditor__rowsError"
                    />
                ) : !hasTruth && drafts.length === 0 ? (
                    <div
                        className="flex flex-col items-center gap-2 py-8 text-center"
                        data-testid="RowEditor__emptyState"
                    >
                        <p className="text-sm text-muted-foreground max-w-sm">
                            No rows yet. Seed the grid from the model&apos;s own
                            extraction, then review and correct it.
                        </p>
                        <Button
                            type="button"
                            size="sm"
                            disabled={
                                suggested.isFetching ||
                                (modelClass !== null &&
                                    modelClass !== LINE_ITEM_MODEL_CLASS)
                            }
                            onClick={() => void applySuggestions()}
                            data-testid="RowEditor__prefillButton"
                        >
                            <Sparkles className="h-3.5 w-3.5 mr-1" />
                            {suggested.isFetching
                                ? 'Loading extraction…'
                                : 'Prefill from model extraction'}
                        </Button>
                    </div>
                ) : (
                    <div
                        className="overflow-x-auto"
                        ref={gridRef}
                        onFocus={handleGridFocus}
                        onKeyDown={handleGridKeyDown}
                    >
                        {(hiddenColumnCount > 0 || showAllColumns) &&
                            modelClass === LINE_ITEM_MODEL_CLASS && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="mb-2"
                                    onClick={() => setShowAllColumns((v) => !v)}
                                    data-testid="RowEditor__columnsToggle"
                                >
                                    {showAllColumns
                                        ? 'Show fewer columns'
                                        : `Show all columns (+${hiddenColumnCount})`}
                                </Button>
                            )}
                        {unusedRowCount > 0 && (
                            <p
                                className="mb-2 text-xs text-muted-foreground"
                                data-testid="RowEditor__unusedSummary"
                            >
                                {unusedRowCount === 1
                                    ? '1 row has'
                                    : `${unusedRowCount} rows have`}{' '}
                                an empty required field, so{' '}
                                {schema?.fieldGroup?.name ?? 'the custom fields'}{' '}
                                is not used for {unusedRowCount === 1 ? 'it' : 'them'}{' '}
                                until filled. Saving still works.
                            </p>
                        )}
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead
                                        className={`${ROW_NUMBER_CELL_CLASS} z-20 text-xs`}
                                    >
                                        #
                                    </TableHead>
                                    {visibleCols.map((c, i) => (
                                        <TableHead
                                            key={c.key}
                                            className={`whitespace-nowrap text-xs ${
                                                i === 0
                                                    ? `${FROZEN_COLUMN_CLASS} z-20`
                                                    : ''
                                            }`}
                                        >
                                            <Hint
                                                text={c.description}
                                                className="inline-block"
                                            >
                                                <span>
                                                    {c.label}
                                                    {c.custom && c.required && (
                                                        <span aria-hidden> *</span>
                                                    )}
                                                    {c.readOnly && (
                                                        <span className="block font-normal text-muted-foreground">
                                                            from an earlier version
                                                        </span>
                                                    )}
                                                </span>
                                            </Hint>
                                        </TableHead>
                                    ))}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {drafts.map((row, rowIndex) => (
                                    <RowCells
                                        key={row.key}
                                        row={row}
                                        rowNumber={rowIndex + 1}
                                        requiredKeys={requiredKeys}
                                        showCustomErrors={saveAttempted}
                                        unusedFor={emptyRequiredLabels(
                                            customColumns,
                                            row.payload,
                                        ).join(', ')}
                                        columns={visibleCols}
                                        isLastRow={
                                            rowIndex === drafts.length - 1
                                        }
                                        onChange={setCell}
                                        onRemove={removeDraft}
                                        onPasteBlock={onPasteBlock}
                                        onFillDown={onFillDown}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}

                {noExtractionAvailable && (
                    <p className="text-xs text-muted-foreground mt-2">
                        No model extraction available for this document.
                    </p>
                )}

                <div className="flex items-center gap-2 mt-3">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={
                            isLoading || schemaIsError || existing === undefined
                        }
                        onClick={() => addDraft(makeBlankDraftRow)}
                    >
                        <Plus className="h-3.5 w-3.5 mr-1" /> Add row
                    </Button>

                    <AlertDialog
                        open={resetDialogOpen}
                        onOpenChange={setResetDialogOpen}
                    >
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={
                                !hasTruth ||
                                isLoading ||
                                schemaIsError ||
                                suggested.isFetching ||
                                (modelClass !== null &&
                                    modelClass !== LINE_ITEM_MODEL_CLASS)
                            }
                            onClick={() => setResetDialogOpen(true)}
                            data-testid="RowEditor__resetButton"
                        >
                            Reset to model answer
                        </Button>
                        <AlertDialogContent data-testid="RowEditor__resetDialog">
                            <AlertDialogHeader>
                                <AlertDialogTitle>
                                    Reset to the model&apos;s extraction?
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                    This replaces every row in the grid with the
                                    model&apos;s own extraction, discarding any
                                    local edits that haven&apos;t been saved.
                                    Nothing is saved until you click Save rows.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel data-testid="RowEditor__resetCancelButton">
                                    Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction
                                    onClick={onConfirmReset}
                                    data-testid="RowEditor__resetConfirmButton"
                                >
                                    Reset
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </div>

            <EditorFooter
                errors={
                    schemaIsError
                        ? [
                              ...errors,
                              'Row schema failed to load, so Save is off.',
                          ]
                        : errors
                }
                onSave={onSaveClick}
                isPending={save.isPending}
                disabled={existing === undefined}
                loadError={rowsLoadFailed ? loadErrorMessage('rows') : null}
                clearCount={clearCount}
                clearNoun="row"
                conflict={conflict}
                onReload={reloadSaved}
                saveLabel="Save rows"
                testId="RowEditor__saveButton"
            />
        </div>
    );
}

/**
 * One table row of editable cells, wrapped in `React.memo` so editing one row
 * doesn't re-render every other row. This relies on stable props: the parent
 * passes `useCallback`-memoised `onChange`/`onRemove`/`onPasteBlock`/
 * `onFillDown`, a `useMemo`-stable `columns` array, and `setCell` rebuilds
 * only the edited row's object (so untouched rows keep the same `row`
 * reference and bail out of re-render). `isLastRow` is a plain boolean that
 * only flips for the (up to) two rows affected by an add/remove, not on every
 * keystroke. Row counts can be large, hence the memoisation.
 *
 * Keyboard navigation (`useGridRovingFocus`) never threads an "active cell"
 * prop through here, so this memo is untouched by arrow/Tab/Enter presses too
 * — not just preserved for the two involved rows. `renderCount` exists purely
 * so tests can assert that (see `RowEditor.keyboardNav.test.tsx`); it has no
 * behavioural effect.
 */
const RowCells = memo(function RowCells({
    row,
    rowNumber,
    requiredKeys,
    showCustomErrors,
    unusedFor,
    columns,
    isLastRow,
    onChange,
    onRemove,
    onPasteBlock,
    onFillDown,
}: {
    row: DraftRow;
    rowNumber: number;
    requiredKeys: ReadonlySet<string>;
    /** Whether custom columns report their errors (after a save attempt). */
    showCustomErrors: boolean;
    /** Required custom fields this row leaves empty, comma-joined; '' when its group is complete. */
    unusedFor: string;
    columns: ColumnDef[];
    isLastRow: boolean;
    onChange: (rowKey: string, field: string, value: unknown) => void;
    onRemove: (rowKey: string) => void;
    onPasteBlock: (
        rowKey: string,
        colKey: string,
        clipboardText: string,
    ) => void;
    onFillDown: (
        rowKey: string,
        colKey: string,
        kind: ColumnDef['kind'],
    ) => void;
}) {
    const renderCount = useRef(0);
    renderCount.current += 1;

    return (
        <TableRow
            data-testid={`RowEditor__row-${row.key}`}
            data-render-count={renderCount.current}
        >
            <TableCell className={`${ROW_NUMBER_CELL_CLASS} z-10 align-top`}>
                <div className="flex h-8 items-center gap-1">
                    <span className="w-6 text-right text-xs tabular-nums text-muted-foreground">
                        {rowNumber}
                    </span>
                    {unusedFor && (
                        <Hint
                            text={`Custom fields not used until filled: ${unusedFor}. Saving still works.`}
                            focusable
                        >
                            <span
                                role="img"
                                aria-label={`Custom fields not used until filled: ${unusedFor}`}
                                data-testid="RowEditor__rowUnused"
                                className="text-muted-foreground"
                            >
                                <CircleDashed className="h-3.5 w-3.5" />
                            </span>
                        </Hint>
                    )}
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        onClick={() => onRemove(row.key)}
                        aria-label="Remove row"
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                </div>
            </TableCell>
            {columns.map((c, i) => {
                const errorMessage = cellErrorMessage(
                    c,
                    row.payload[c.key],
                    requiredKeys,
                    showCustomErrors,
                );
                const errorId = errorMessage
                    ? `${row.key}-${c.key}-error`
                    : undefined;
                return (
                    <TableCell
                        key={c.key}
                        className={`align-top ${i === 0 ? `${FROZEN_COLUMN_CLASS} z-10` : ''}`}
                        onPaste={(e: ClipboardEvent<HTMLTableCellElement>) => {
                            // Take over the paste entirely: without this, the
                            // browser would also insert the raw multi-cell TSV
                            // blob into the focused `Input`'s native value, which
                            // then races the `setDrafts` batch below via the
                            // input's own `onChange`.
                            e.preventDefault();
                            onPasteBlock(
                                row.key,
                                c.key,
                                e.clipboardData.getData('text/plain'),
                            );
                        }}
                    >
                        <div className="flex items-start gap-1">
                            <div className="flex-1 min-w-0">
                                <Cell
                                    kind={c.kind}
                                    value={row.payload[c.key]}
                                    onChange={(v) =>
                                        onChange(row.key, c.key, v)
                                    }
                                    errorId={errorId}
                                    column={c}
                                />
                            </div>
                            <Hint
                                text="Fill down"
                                focusable={isLastRow}
                                className={c.readOnly ? 'hidden' : 'inline-flex'}
                            >
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 shrink-0"
                                    disabled={isLastRow}
                                    onClick={() =>
                                        onFillDown(row.key, c.key, c.kind)
                                    }
                                    aria-label={`Fill ${c.label} down`}
                                    hidden={c.readOnly}
                                >
                                    <ArrowDown className="h-3.5 w-3.5" />
                                </Button>
                            </Hint>
                        </div>
                        {errorId && (
                            <p
                                id={errorId}
                                className="mt-1 text-xs text-destructive"
                            >
                                {errorMessage}
                            </p>
                        )}
                    </TableCell>
                );
            })}
        </TableRow>
    );
});

/** A required column, as needed by {@link validateDrafts}; `kind` defaults to text. */
export interface RequiredColumn {
    key: string;
    label: string;
    kind?: ColumnDef['kind'];
}

/**
 * The inline error under a cell, or `undefined`. A built-in required column
 * errors whenever it is blank, as it always has; a custom column (required or
 * with limits) only once `showCustomErrors` is on, so an annotation saved under
 * an older field-group version opens clean.
 */
function cellErrorMessage(
    column: ColumnDef,
    value: unknown,
    requiredKeys: ReadonlySet<string>,
    showCustomErrors: boolean,
): string | undefined {
    if (column.custom) {
        return showCustomErrors
            ? (customValueError(column, value) ?? undefined)
            : undefined;
    }
    return requiredKeys.has(column.key) && isBlankRequired(value, column.kind)
        ? `${column.label} is required`
        : undefined;
}

/**
 * Minimal client-side gate; the server is the authority (422 → toast).
 *
 * `requiredColumns` comes from the doc's resolved row schema (not a hardcoded
 * item_no/description pair), so a status-report doc validates its own
 * required fields instead of LineItem's. A required cell must be filled for
 * its kind: a non-blank string, or any number, boolean or choice.
 *
 * `customColumns` are the reviewer-defined columns: when `checkCustom` is on
 * each cell is checked against its type and limits too (`checkCustom` is off
 * until a save has been attempted, so older annotations open without errors).
 * A blank custom string is sent as null either way.
 *
 * Returns both the blocking errors AND the server-ready payload: rows are sent
 * RAW (snake_case). The computed `mro_refs` field is stripped (the server
 * rejects/excludes it) but every other key — including untouched `meta` /
 * `field_verifications` and earlier-version values — is preserved intact. The
 * payload is built eagerly here, but the grid hook only sends it once `errors`
 * is empty.
 */
export function validateDrafts(
    drafts: DraftRow[],
    requiredColumns: RequiredColumn[],
    customColumns: ColumnDef[] = [],
    checkCustom = true,
): GridValidation<GtRowPayload[]> {
    const errors: string[] = [];
    drafts.forEach((d, i) => {
        for (const col of requiredColumns) {
            const value = d.payload[col.key];
            if (isBlankRequired(value, col.kind ?? 'text')) {
                errors.push(`Row ${i + 1}: ${col.label} is required.`);
            }
        }
        if (!checkCustom) return;
        for (const col of customColumns) {
            const message = customValueError(col, d.payload[col.key]);
            if (message) errors.push(`Row ${i + 1}: ${message}`);
        }
    });

    const customKeys = new Set(customColumns.map((c) => c.key));
    const payload: GtRowPayload[] = drafts.map((d) => {
        const { mro_refs: _omit, ...rest } = d.payload as Record<
            string,
            unknown
        >;
        return nullBlankCustomValues(rest, customKeys);
    });

    return { errors, payload };
}
